import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { projects, projectOrder } from '../data/projects';

export function CaseStudiesPreview() {
  const { currentLanguage } = useLanguage();

  const content = {
    en: { 
      label: 'Selected Projects',
      title: 'Some of Our Work', 
      subtitle: 'Systems we\'ve engineered across industries.',
      viewAll: 'See More' 
    },
    tr: { 
      label: 'Seçili Projeler',
      title: 'Bazı İşlerimiz', 
      subtitle: 'Farklı sektörlerde geliştirdiğimiz sistemler.',
      viewAll: 'Daha Fazla' 
    },
    de: { 
      label: 'Ausgewählte Projekte',
      title: 'Einige unserer Arbeiten', 
      subtitle: 'Systeme, die wir branchenübergreifend entwickelt haben.',
      viewAll: 'Mehr sehen' 
    }
  };

  const t = content[currentLanguage] || content.en;

  // Show only first 3 projects - first one is featured
  const previewProjects = projectOrder.slice(0, 3);
  const featuredProjectId = previewProjects[0];
  const featuredProject = projects[featuredProjectId];
  const otherProjects = previewProjects.slice(1);

  return (
    <section
      id="case-studies"
      data-testid="case-studies-preview"
      className="relative py-20 md:py-28 bg-white"
    >
      <div className="relative z-10 max-w-5xl mx-auto px-4 md:px-8">
        {/* Header */}
        <div className="text-center mb-12 md:mb-14">
          <p 
            className="text-xs uppercase tracking-widest mb-4"
            style={{ color: '#0F766E' }}
          >
            {t.label}
          </p>
          <h2 
            className="text-3xl md:text-4xl font-semibold tracking-tight mb-3"
            style={{ color: 'var(--text-on-light-primary)' }}
          >
            {t.title}
          </h2>
          <p 
            className="text-base md:text-lg"
            style={{ color: 'var(--text-on-light-muted)' }}
          >
            {t.subtitle}
          </p>
        </div>

        {/* Asymmetric Layout: Featured (large) + Two smaller */}
        <div className="grid md:grid-cols-5 gap-6 mb-10">
          {/* Featured Project - Takes 3 columns */}
          <Link
            to={`/case-studies/${featuredProjectId}`}
            data-testid={`preview-${featuredProjectId}`}
            className="md:col-span-3 group block rounded-2xl transition-all duration-300 hover:border-[#0F766E] overflow-hidden"
            style={{ 
              background: 'linear-gradient(135deg, rgba(15, 118, 110, 0.08) 0%, rgba(15, 118, 110, 0.02) 100%)',
              border: '1px solid rgba(15, 118, 110, 0.15)'
            }}
          >
            <div className="p-6 md:p-8">
              {/* Category & Year */}
              <div className="flex items-center justify-between mb-6">
                <span 
                  className="text-xs uppercase tracking-wider px-2 py-1 rounded"
                  style={{ 
                    color: '#0F766E', 
                    background: 'rgba(15, 118, 110, 0.1)'
                  }}
                >
                  {featuredProject.category[currentLanguage] || featuredProject.category.en}
                </span>
                <span 
                  className="text-xs"
                  style={{ color: 'var(--text-on-light-subtle)', fontFamily: 'JetBrains Mono, monospace' }}
                >
                  {featuredProject.year}
                </span>
              </div>
              
              {/* Project name */}
              <h3 
                className="text-2xl md:text-3xl font-semibold mb-3 group-hover:text-[#0F766E] transition-colors"
                style={{ color: 'var(--text-on-light-primary)' }}
              >
                {featuredProject.name}
              </h3>
              
              {/* Tagline */}
              <p 
                className="text-base mb-6"
                style={{ color: 'var(--text-on-light-muted)' }}
              >
                {featuredProject.tagline[currentLanguage] || featuredProject.tagline.en}
              </p>

              {/* Key Metrics - Horizontal */}
              {featuredProject.performance && (
                <div 
                  className="pt-6 border-t flex flex-wrap gap-8"
                  style={{ borderColor: 'rgba(15, 118, 110, 0.15)' }}
                >
                  {Object.entries(featuredProject.performance).slice(0, 2).map(([key, item], idx) => (
                    <div key={key}>
                      <span 
                        className="text-2xl md:text-3xl font-bold"
                        style={{ color: '#0F766E' }}
                      >
                        {item.value}
                      </span>
                      <span 
                        className="text-xs uppercase tracking-wider ml-2"
                        style={{ color: 'var(--text-on-light-muted)' }}
                      >
                        {item.label[currentLanguage] || item.label.en}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </Link>

          {/* Other Projects - Takes 2 columns, stacked */}
          <div className="md:col-span-2 flex flex-col gap-6">
            {otherProjects.map((projectId) => {
              const project = projects[projectId];
              return (
                <Link
                  key={projectId}
                  to={`/case-studies/${projectId}`}
                  data-testid={`preview-${projectId}`}
                  className="group block p-5 rounded-xl transition-all duration-300 hover:border-[#0F766E] flex-1"
                  style={{ 
                    background: '#F8FAFB',
                    border: '1px solid rgba(15, 118, 110, 0.1)'
                  }}
                >
                  {/* Category */}
                  <span 
                    className="text-[10px] uppercase tracking-wider"
                    style={{ color: '#0F766E' }}
                  >
                    {project.category[currentLanguage] || project.category.en}
                  </span>
                  
                  {/* Project name */}
                  <h3 
                    className="text-lg font-semibold mt-2 mb-2 group-hover:text-[#0F766E] transition-colors"
                    style={{ color: 'var(--text-on-light-primary)' }}
                  >
                    {project.name}
                  </h3>
                  
                  {/* Tagline */}
                  <p 
                    className="text-sm line-clamp-2"
                    style={{ color: 'var(--text-on-light-muted)' }}
                  >
                    {project.tagline[currentLanguage] || project.tagline.en}
                  </p>

                  {/* Key Metric */}
                  {project.performance && (
                    <div className="mt-4 flex items-baseline gap-2">
                      <span 
                        className="text-xl font-bold"
                        style={{ color: '#0F766E' }}
                      >
                        {Object.values(project.performance)[0]?.value}
                      </span>
                      <span 
                        className="text-[10px] uppercase tracking-wider"
                        style={{ color: 'var(--text-on-light-subtle)' }}
                      >
                        {Object.values(project.performance)[0]?.label[currentLanguage] || Object.values(project.performance)[0]?.label.en}
                      </span>
                    </div>
                  )}
                </Link>
              );
            })}
          </div>
        </div>

        {/* View All CTA */}
        <div className="text-center">
          <Link
            to="/case-studies"
            data-testid="view-all-projects"
            className="inline-flex items-center gap-2 text-sm font-medium transition-all duration-300 hover:gap-3"
            style={{ color: '#0F766E' }}
          >
            {t.viewAll}
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </section>
  );
}
